/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Logger } from './Logger';

/**
 * Executes callbacks with exponential backoff
 */
export class ExponentialBackoff {
    private backoffId = 0;
    private backoffCount = 0;
    private timerHandle?: number;
    private callback?: () => void;

    public constructor(private logger: Logger, private maxBackoffInMs: number) {}

    private static calculateNextBackoffMs(backoffCount: number, maxBackoffInMs: number): number {
        // tslint:disable-next-line:insecure-random
        const fudge = 1 + (Math.random() - 0.5) * 0.4; // +/- 20%
        let backoff = 1000 * Math.pow(2, backoffCount) * fudge; // 1s initial
        backoff = Math.round(backoff);

        return Math.min(maxBackoffInMs, backoff);
    }

    public setMaxBackoffMs(ms: number) {
        this.maxBackoffInMs = ms;
    }

    public backoff(taskName: string, handler: () => void): void {
        if (this.timerHandle !== undefined) {
            this.logger.debug('Clearing current back off');
            clearTimeout(this.timerHandle);
            this.timerHandle = undefined;
        }

        const backoffMs = ExponentialBackoff.calculateNextBackoffMs(this.backoffCount, this.maxBackoffInMs);
        this.backoffId++;
        this.backoffCount++;
        this.logger.info(`Backing off ${taskName} for ${backoffMs} milliseconds with ID ${this.backoffId}`);
        this.callback = () => {
            this.logger.info(`Back off for ${taskName} with ID ${this.backoffId} complete, invoking handler`);
            this.timerHandle = undefined;
            this.callback = undefined;
            handler();
        };
        this.timerHandle = setTimeout(this.callback, backoffMs);
    }

    /// Stop the backoff if active, do not call the handler and reset to the initial/shortest period again
    public reset(): void {
        if (this.timerHandle !== undefined) {
            this.logger.debug(`Resetting back off with ID ${this.backoffId}`);
            clearTimeout(this.timerHandle);
            this.timerHandle = undefined;
            this.callback = undefined;
        }
        this.backoffCount = 0;
    }

    /// Call the handler right now if active and reset to the initial/shortest period again
    public expediteIfPending(): void {
        this.backoffCount = 0;
        if (this.timerHandle !== undefined) {
            this.logger.debug(`Expediting back off with ID ${this.backoffId}`);
            clearTimeout(this.timerHandle);
            this.timerHandle = undefined;
            const callback = this.callback;
            this.callback = undefined;
            if (callback) {
                callback();
            }
        }
    }
}



// WEBPACK FOOTER //
// ./lib_temp/ExponentialBackoff.ts